/**
 * Gömülü SVG ikonlar — tek tip çizgi ikon ailesi (Dalga 3, B-4).
 *
 * ## Neden paket değil?
 *
 * Bu bileşen ilk yazıldığında `lucide-react` kullanıyordu. Gerçek sunucuda
 * (`/opt/pbfork`) kurulum şöyle kırıldı:
 *
 * ```
 * error during build:
 * [vite]: Rolldown failed to resolve import "lucide-react"
 *   from "apps/web/src/components/ItemCard.tsx"
 * ```
 *
 * Sebep basit ama öğretici: yeni bir bağımlılık, her hedef makinede
 * (`git pull` sonrası) **ayrıca `npm install`** demektir. Sunucu bir VPS'te
 * yaşıyor ve dağıtım "çek → derle → yeniden başlat" olmalı; araya bağımlılık
 * kurulumu girdiğinde her güncelleme ayrı bir adım ve ayrı bir hata noktası
 * olur.
 *
 * ## Erişilebilirlik
 *
 * İkonlar `<title>` etiketi taşır; böylece hem standart SVG erişilebilirliği
 * sağlanır hem de ekran okuyucular için anlamlı etiket üretilir.
 *
 * ## Tasarım Özellikleri
 *
 * Dalga 3'te kalan emoji/ikonlar tek bir aileye taşındı. İkon ızgarası 24 px,
 * kontur 2, `currentColor` ile renklenir.
 */
import type { ReactNode, SVGProps } from 'react';

/** Tüm ikonlar paylaşır: ızgara, kontur kalınlığı, köşe biçimi. */
const BASE = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
} as const;

export type IconProps = { size?: number; className?: string; title?: string } & Omit<
  SVGProps<SVGSVGElement>,
  'children'
>;

/** Oynatma üçgeni (dolu). */
export function PlayIcon({ size = 24, className, title = 'Oynat', ...rest }: IconProps): ReactNode {
  return (
    <svg
      {...BASE}
      {...rest}
      className={className}
      width={size}
      height={size}
      fill="currentColor"
      stroke="none"
    >
      <title>{title}</title>
      <path d="M8 5.14v13.72L19 12z" />
    </svg>
  );
}

/** Ana sayfa ikonu. */
export function HomeIcon({
  size = 24,
  className,
  title = 'Ana Sayfa',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  );
}

/** Film ikonu. */
export function FilmIcon({ size = 24, className, title = 'Film', ...rest }: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
      <line x1="7" y1="2" x2="7" y2="22" />
      <line x1="17" y1="2" x2="17" y2="22" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="2" y1="7" x2="7" y2="7" />
      <line x1="2" y1="17" x2="7" y2="17" />
      <line x1="17" y1="17" x2="22" y2="17" />
      <line x1="17" y1="7" x2="22" y2="7" />
    </svg>
  );
}

/** Dizi / TV ikonu. */
export function TvIcon({ size = 24, className, title = 'Dizi', ...rest }: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <rect x="2" y="7" width="20" height="15" rx="2" ry="2" />
      <polyline points="17 2 12 7 7 2" />
    </svg>
  );
}

/** Müzik ikonu. */
/** Çalma sırası: satırlar + küçük oynatma üçgeni. */
export function QueueIcon({
  size = 24,
  className,
  title = 'Çalma sırası',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M4 6h11M4 12h11M4 18h7" />
      <path d="m16 14 4 3-4 3z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Karıştır. */
export function ShuffleIcon({
  size = 24,
  className,
  title = 'Karıştır',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M3 7h4l10 10h4" />
      <path d="m18 14 3 3-3 3" />
      <path d="M3 17h4l2-2" />
      <path d="m14 7 2-2h4" />
      <path d="m18 2 3 3-3 3" />
    </svg>
  );
}

/** Tekrar. */
export function RepeatIcon({
  size = 24,
  className,
  title = 'Tekrar',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="m17 2 3 3-3 3" />
      <path d="M4 11V9a4 4 0 0 1 4-4h12" />
      <path d="m7 22-3-3 3-3" />
      <path d="M20 13v2a4 4 0 0 1-4 4H4" />
    </svg>
  );
}

export function MusicIcon({
  size = 24,
  className,
  title = 'Müzik',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M9 18V5l12-2v13" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="18" cy="16" r="3" />
    </svg>
  );
}

/** Klasör / Kütüphane ikonu. */
export function FolderIcon({
  size = 24,
  className,
  title = 'Klasör',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  );
}

/** Ayarlar / Dişli ikonu. */
export function SettingsIcon({
  size = 24,
  className,
  title = 'Ayarlar',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

/** Artı / Ekleme ikonu. */
export function PlusIcon({ size = 24, className, title = 'Ekle', ...rest }: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

/** Çıkış ikonu. */
export function LogOutIcon({
  size = 24,
  className,
  title = 'Çıkış',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

/** Arama ikonu. */
export function SearchIcon({ size = 24, className, title = 'Ara', ...rest }: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/** Parıltı / Yeni içerik ikonu. */
export function SparklesIcon({
  size = 24,
  className,
  title = 'Öne Çıkan',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3Z" />
    </svg>
  );
}

/** Çöp kutusu / Silme ikonu. */
export function TrashIcon({ size = 24, className, title = 'Sil', ...rest }: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

/** Yenileme / Tarama ikonu. */
export function RefreshIcon({
  size = 24,
  className,
  title = 'Yenile',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <polyline points="23 4 23 10 17 10" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
  );
}

/** İndirme ikonu (APK güncellemesi). */
export function DownloadIcon({
  size = 24,
  className,
  title = 'İndir',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M12 3v12" />
      <polyline points="7 10 12 15 17 10" />
      <path d="M5 21h14" />
    </svg>
  );
}

/** Kilit ikonu (soğuk katman / ev tüneli rozeti). */
export function LockIcon({
  size = 24,
  className,
  title = 'Kilitli',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

/** Açık kilit ikonu (kilitlenmemiş kanal). */
export function UnlockIcon({
  size = 24,
  className,
  title = 'Kilitli değil',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 9.5-2" />
    </svg>
  );
}

/** Uyarı ikonu (tehlikeli eylem başlığı). */
export function AlertIcon({
  size = 24,
  className,
  title = 'Uyarı',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

/** Bilgi ikonu (açıklama notu). */
export function InfoIcon({ size = 24, className, title = 'Bilgi', ...rest }: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

/** Kullanıcı / hesap ikonu. */
export function UserIcon({
  size = 24,
  className,
  title = 'Kullanıcı',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

/** Taç / yönetici rozeti. */
export function CrownIcon({
  size = 24,
  className,
  title = 'Yönetici',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="m2 18 1.5-9 5 4L12 5l3.5 8 5-4L22 18z" />
    </svg>
  );
}

/** Kalkan / iki faktörlü doğrulama. */
export function ShieldIcon({
  size = 24,
  className,
  title = 'Güvenlik',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

/** Göz (parola göster). */
export function EyeIcon({ size = 24, className, title = 'Göster', ...rest }: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** Göz kapalı (parola gizle). */
export function EyeOffIcon({
  size = 24,
  className,
  title = 'Gizle',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-7 0-11-7-11-7a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

/** Altyazı / altyazı dili ikonu. */
export function CaptionsIcon({
  size = 24,
  className,
  title = 'Altyazı',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <rect x="2" y="4" width="20" height="16" rx="3" />
      <path d="M10 10.5a2.5 2.5 0 1 0 0 3" />
      <path d="M18 10.5a2.5 2.5 0 1 0 0 3" />
    </svg>
  );
}

/** Klavye (kısayollar). */
export function KeyboardIcon({
  size = 24,
  className,
  title = 'Klavye',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <line x1="6" y1="9" x2="6.01" y2="9" />
      <line x1="10" y1="9" x2="10.01" y2="9" />
      <line x1="14" y1="9" x2="14.01" y2="9" />
      <line x1="18" y1="9" x2="18.01" y2="9" />
      <line x1="8" y1="15" x2="16" y2="15" />
    </svg>
  );
}

/**
 * Ses düzeyi: `muted` / `low` / `high`.
 *
 * Üç düzey tek bileşende birleşti: oynatıcı çubuğu ses ikonunu
 * `isSilent ? '🔇' : volume < 0.5 ? '🔉' : '🔊'` ile seçiyordu ve
 * ikon ailesi bu üç durumu ayrı bileşenler olarak taşımak zorunda
 * kalırdı. Dalga sayısı (1 / 2 / 0 kavis) seviyeyi gösterir.
 */
export function VolumeIcon({
  size = 24,
  className,
  level = 'high',
  title,
  ...rest
}: IconProps & { level?: 'muted' | 'low' | 'high' }): ReactNode {
  const label =
    title ?? (level === 'muted' ? 'Sessiz' : level === 'low' ? 'Ses düşük' : 'Ses yüksek');
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{label}</title>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      {level === 'muted' ? (
        <>
          <line x1="23" y1="9" x2="17" y2="15" />
          <line x1="17" y1="9" x2="23" y2="15" />
        </>
      ) : (
        <>
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          {level === 'high' && <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />}
        </>
      )}
    </svg>
  );
}

/** Görsel / PGS (resim tabanlı altyazı). */
export function ImageIcon({
  size = 24,
  className,
  title = 'Görsel',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-4.35-4.35L5 21" />
    </svg>
  );
}

/** Ampul / ipucu. */
export function BulbIcon({ size = 24, className, title = 'İpucu', ...rest }: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 0 1 8.91 14" />
    </svg>
  );
}

/** Şimşek / doğrudan oynatma. */
export function BoltIcon({ size = 24, className, title = 'Hızlı', ...rest }: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

/** Ekran / cihaz listesi. */
export function MonitorIcon({
  size = 24,
  className,
  title = 'Cihaz',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </svg>
  );
}

/** Pano / kopyala. */
export function ClipboardIcon({
  size = 24,
  className,
  title = 'Kopyala',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
    </svg>
  );
}

/** Zincir / dış bağlantı. */
export function LinkIcon({
  size = 24,
  className,
  title = 'Bağlantı',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

/** Kum saati / beklemede. */
export function HourglassIcon({
  size = 24,
  className,
  title = 'Bekleniyor',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M5 2h14" />
      <path d="M5 22h14" />
      <path d="M17 22v-4.17a2 2 0 0 0-.59-1.42L12 12l-4.41 4.41A2 2 0 0 0 7 17.83V22" />
      <path d="M7 2v4.17a2 2 0 0 0 .59 1.42L12 12l4.41-4.41A2 2 0 0 0 17 6.17V2" />
    </svg>
  );
}

/** Saat / segment süresi. */
export function ClockIcon({ size = 24, className, title = 'Süre', ...rest }: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

/** Onay işareti (kopyalandı gibi). */
export function CheckIcon({
  size = 24,
  className,
  title = 'Tamam',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

/** Duraklat (iki dikey çubuk). */
export function PauseIcon({
  size = 24,
  className,
  title = 'Duraklat',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg
      {...BASE}
      {...rest}
      className={className}
      width={size}
      height={size}
      fill="currentColor"
      stroke="none"
    >
      <title>{title}</title>
      <rect x="6" y="4" width="4.5" height="16" rx="1.4" />
      <rect x="13.5" y="4" width="4.5" height="16" rx="1.4" />
    </svg>
  );
}

/**
 * Sıradaki içerik (üçgen + çubuk).
 *
 * Oynatıcının "sıradaki parça / bölüm" düğmesi. Üçgen **dolu**, çubuk
 * **çizgili** — bu, "atla" ikonu olduğunu tek bakışta anlatır.
 */
export function SkipNextIcon({
  size = 24,
  className,
  title = 'Sıradaki',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <polygon points="4.5 4.5 15 12 4.5 19.5" fill="currentColor" stroke="none" />
      <line x1="19" y1="5" x2="19" y2="19" />
    </svg>
  );
}

/**
 * Alev / sıcak katman (hızlı yerel disk).
 *
 * Sıcak–soğuk ayrımı emoji `🔥`/`❄️` ile anlatılıyordu. Renkli bir emoji
 * bazen anlamlı bir renk taşır (soğuk mavi); çizgi ikon ailesi renk
 * taşımadığı için ayrım artık **yalnız** ikon biçimine dayanıyor — bu yüzden
 * biri yukarı bakıp diğeri aşağı, tam ters yönlerde.
 */
export function FlameIcon({
  size = 24,
  className,
  title = 'Sıcak Katman',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M12 22a7 7 0 0 0 7-7c0-4-3-6.5-4.5-9.5C13 7 12 8 12 8s-1-3-3.5-5C8 6 5 9 5 15a7 7 0 0 0 7 7z" />
    </svg>
  );
}

/** Kar tanesi / soğuk katman (ev tüneli). */
export function SnowIcon({
  size = 24,
  className,
  title = 'Soğuk Katman',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <line x1="12" y1="2" x2="12" y2="22" />
      <line x1="4" y1="7" x2="20" y2="17" />
      <line x1="20" y1="7" x2="4" y2="17" />
      <line x1="12" y1="2" x2="9.5" y2="4.5" />
      <line x1="12" y1="2" x2="14.5" y2="4.5" />
      <line x1="12" y1="22" x2="9.5" y2="19.5" />
      <line x1="12" y1="22" x2="14.5" y2="19.5" />
    </svg>
  );
}

/** Tam ekran — köşeler dışa doğru açılır. */
export function FullscreenIcon({
  size = 24,
  className,
  title = 'Tam ekran',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
      <path d="M3 16v3a2 2 0 0 0 2 2h3" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </svg>
  );
}

/** Tam ekrandan çık — köşeler içe doğru kapanır. */
export function FullscreenExitIcon({
  size = 24,
  className,
  title = 'Tam ekrandan çık',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M8 3v3a2 2 0 0 1-2 2H3" />
      <path d="M21 8h-3a2 2 0 0 1-2-2V3" />
      <path d="M3 16h3a2 2 0 0 1 2 2v3" />
      <path d="M16 21v-3a2 2 0 0 1 2-2h3" />
    </svg>
  );
}

/** Çarpı / kapat. */
export function CloseIcon({
  size = 24,
  className,
  title = 'Kapat',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/** Üç nokta — taşma / ikincil eylem menüsü. */
export function MoreIcon({
  size = 24,
  className,
  title = 'Diğer işlemler',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <circle cx="5" cy="12" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
    </svg>
  );
}

/** 10 saniye geri — saat yönünün tersine dönen ok. */
export function Replay10Icon({
  size = 24,
  className,
  title = '10 saniye geri',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M3 12a9 9 0 1 0 3-6.7" />
      <path d="M3 4v5h5" />
    </svg>
  );
}

/** 10 saniye ileri — saat yönünde dönen ok. */
export function Forward10Icon({
  size = 24,
  className,
  title = '10 saniye ileri',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M21 12a9 9 0 1 1-3-6.7" />
      <path d="M21 4v5h-5" />
    </svg>
  );
}

/** Aşağı ok. */
export function ChevronDownIcon({
  size = 24,
  className,
  title = 'Aşağı',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/** Yukarı ok. */
export function ChevronUpIcon({
  size = 24,
  className,
  title = 'Yukarı',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="m6 15 6-6 6 6" />
    </svg>
  );
}

/** Sol ok. */
export function ArrowLeftIcon({
  size = 24,
  className,
  title = 'Geri',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M19 12H5" />
      <path d="m12 19-7-7 7-7" />
    </svg>
  );
}

/** Sağ ok. */
export function ArrowRightIcon({
  size = 24,
  className,
  title = 'İleri',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

/** Yıldız (boş) — "Favorilere ekle". */
export function StarIcon({
  size = 24,
  className,
  title = 'Favorilere ekle',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9L12 3.5Z" />
    </svg>
  );
}

/** Yıldız (dolu) — "Favoride". */
export function StarFilledIcon({
  size = 24,
  className,
  title = 'Favoride',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size} fill="currentColor">
      <title>{title}</title>
      <path d="M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9L12 3.5Z" />
    </svg>
  );
}

/** Yer imi (boş) — "Listeme ekle". */
export function BookmarkIcon({
  size = 24,
  className,
  title = 'Listeme ekle',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg {...BASE} {...rest} className={className} width={size} height={size}>
      <title>{title}</title>
      <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1Z" />
    </svg>
  );
}

/** Yer imi (dolu) — "Listemde". */
export function BookmarkFilledIcon({
  size = 24,
  className,
  title = 'Listemde',
  ...rest
}: IconProps): ReactNode {
  return (
    <svg
      {...BASE}
      {...rest}
      className={className}
      width={size}
      height={size}
      fill="currentColor"
      stroke="none"
    >
      <title>{title}</title>
      <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1Z" />
    </svg>
  );
}
